/* eslint-disable formatjs/no-literal-string-in-jsx */
import Button from '@atlaskit/button';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { token } from '@atlaskit/tokens';
import { SkeletonContext, SkeletonItem } from '@databases/skeleton';
import classNames from 'classnames';
import { range } from 'lodash-es';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { TABLE_CLASSNAMES } from '../../../constants';
import { IGNORED_TABLE_ELEMENT_CLASS, TableResizerContainer } from '../TableResizer';
import FieldNameContainer from '../database-field/field-name/FieldNameContainer';
import {
  FieldNameInlineContainer,
  FieldNameWithActionsContainer,
} from '../database-field/field-name/FieldNameWithActions';
import { StyledInlineEditWrapper } from '../database-value/DatabaseValueInlineEdit';
import { DatabaseValueViewContainer } from '../database-value/utils';
import { ButtonGroup } from '../styled';

import DatabaseContentWrapper from './DatabaseContentWrapper';
import { Container as ToolbarContainer } from './Toolbar';
import { Table, TableLayoutWrapper } from './table-layout/Table';
import { Th } from './table-layout/TableLayoutHeaderCell';
import { Tr } from './table-layout/TableLayoutRow';
import { SearchFieldTransition } from './view-settings-picker/QuickSearch';

const StyledToolbarContainer = styled(ToolbarContainer)`
  padding-bottom: 2px !important;
  border-bottom: 1px solid ${token('color.border')} !important;
`;

const DatabaseContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-height: 0;
`;

const DatabaseContentSkeletonWrapper = styled(DatabaseContentContainer)`
  * {
    user-select: none;
    border: none;
    outline: none;
    cursor: default;
  }

  > ${DatabaseContentWrapper} > div {
    // hide scrollbars
    overflow: visible;
  }

  &&& .${TABLE_CLASSNAMES.ADD_FIELD} {
    right: 50px;
  }

  .add-entry {
    grid-column: 1 / -1;
    padding: 10px 8px;
  }
`;

const VerticalDivider = styled.div`
  width: 1px;
  background-color: ${token('color.border')};
  margin-left: 16px;
`;

const ViewTabsWrapper = styled.div`
  height: 24px;
  display: flex;
  margin-left: 10px;
`;

export const DatabaseTableSkeleton: FunctionComponent = () => {
  return (
    <SkeletonContext isEnabled={true}>
      <DatabaseContentSkeletonWrapper>
        <DatabaseContentWrapper hasCalulations={false}>
          <TableLayoutWrapper containerWidth="100%">
            <TableResizerContainer>
              <Table
                gridTemplateColumns="1fr 1fr 1fr 1fr 1px"
                minWidth={624}
                actionsMidWidth={1}
                containerHeight={600}
              >
                <thead>
                  <tr>
                    {range(4).map((i) => (
                      <Th isDragOver={false} containerHeight={0} key={i}>
                        <FieldNameWithActionsContainer>
                          <FieldNameInlineContainer>
                            <FieldNameContainer>
                              <SkeletonItem hasIcon minWidth={80}>
                                Field Name
                              </SkeletonItem>
                            </FieldNameContainer>
                          </FieldNameInlineContainer>
                        </FieldNameWithActionsContainer>
                      </Th>
                    ))}
                    <th
                      className={classNames([TABLE_CLASSNAMES.ADD_FIELD], {
                        [IGNORED_TABLE_ELEMENT_CLASS]: true,
                      })}
                    >
                      <SkeletonItem isButton height={20} width={20}></SkeletonItem>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {range(4).map((i) => {
                    const content =
                      i % 2 === 0 ? 'xxxxxxxxxxxxxxxxxxx' : 'xxxxxxxxxxxxxxx';
                    return (
                      <Tr
                        isInitialized
                        isFilterOrSortingActive={false}
                        key={i}
                        insertIndicatorPosition={undefined}
                      >
                        {range(4).map((i) => (
                          <th key={i}>
                            <StyledInlineEditWrapper>
                              <DatabaseValueViewContainer>
                                <SkeletonItem>{content}</SkeletonItem>
                              </DatabaseValueViewContainer>
                            </StyledInlineEditWrapper>
                          </th>
                        ))}
                        <td
                          className={classNames([TABLE_CLASSNAMES.ACTIONS], {
                            [IGNORED_TABLE_ELEMENT_CLASS]: true,
                          })}
                        />
                      </Tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="add-entry">
                      <SkeletonItem hasIcon maxWidth={80}>
                        Add entry
                      </SkeletonItem>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </TableResizerContainer>
          </TableLayoutWrapper>
        </DatabaseContentWrapper>
      </DatabaseContentSkeletonWrapper>
    </SkeletonContext>
  );
};

const DatabaseContentSkeleton: FunctionComponent = () => {
  const viewTabs = (
    <ViewTabsWrapper>
      <SkeletonItem hasIcon height={16}>
        View Tab Elem Name
      </SkeletonItem>
      <VerticalDivider />
      <SkeletonItem isButton height={16} width={16}>
        <Button>Add</Button>
      </SkeletonItem>
    </ViewTabsWrapper>
  );

  const viewSettingsButtons = (
    <ButtonGroup style={{ marginBottom: 6, marginLeft: 10, marginTop: 6 }}>
      {range(4).map((i) => (
        <SkeletonItem isButton height={24} key={i}>
          <Button>View Button</Button>
        </SkeletonItem>
      ))}
    </ButtonGroup>
  );

  const toolbar = (
    <ButtonGroup align="spaced" style={{ marginBottom: 10 }}>
      <ButtonGroup>
        <SkeletonItem isButton height={16}>
          <SearchFieldTransition expanded={false}>
            <Button iconBefore={<SearchIcon label="" />}>Search</Button>
          </SearchFieldTransition>
        </SkeletonItem>
        <SkeletonItem isButton height={16}>
          <Button />
        </SkeletonItem>
      </ButtonGroup>
    </ButtonGroup>
  );

  return (
    <SkeletonContext isEnabled={true}>
      <DatabaseContentSkeletonWrapper>
        <StyledToolbarContainer>
          {viewTabs}
          <div className="expand" />
          {toolbar}
        </StyledToolbarContainer>

        {viewSettingsButtons}

        <DatabaseTableSkeleton />
      </DatabaseContentSkeletonWrapper>
    </SkeletonContext>
  );
};

export default DatabaseContentSkeleton;
